/**
 * Consultas y mutaciones contra la API.
 *
 * Las claves de cache incluyen el idioma: el contenido llega ya traducido del
 * servidor, asi que al cambiar de idioma hay que volver a pedirlo y no reusar
 * lo que habia.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type {
  DatosRegistro,
  DatosRespuesta,
  DatosVisita,
  Idioma,
  PreguntaPublica,
  PuntoPublico,
  Ranking,
  RespuestaRegistro,
  RespuestaVisita,
  ResultadoRespuesta,
  SesionParticipante,
} from '@recorregut/shared';
import { api } from './cliente.ts';
import { idiomaActivo } from '@/i18n';

export interface EstadoRecorrido {
  evento_id: number;
  en_curso: boolean;
  puntos_del_evento: Array<{ punto_id: number; evento_punto_id: number }>;
  visitados: number[];
  respondidos: Array<{
    punto_id: number;
    evento_punto_id: number;
    correcta: boolean;
    puntos: number;
  }>;
  puntos_totales: number;
}

export interface EventoPublico {
  id: number;
  nombre: string;
  descripcion: string;
  fecha_hora_inicio: string;
  fecha_hora_fin: string;
  registro_abierto: boolean;
  en_curso: boolean;
  /** Puntos de ESTE recorrido, no los publicados en el mapa. */
  total_puntos: number;
}

function useIdioma(): Idioma {
  // Se lee de i18next en cada render para que la clave de cache cambie a la vez
  // que la interfaz.
  useTranslation();
  return idiomaActivo();
}

export function useSesion() {
  const idioma = useIdioma();
  return useQuery<SesionParticipante | null>({
    queryKey: ['sesion', idioma],
    queryFn: async () => {
      try {
        return await api.get<SesionParticipante>('/api/sesion');
      } catch {
        // Sin sesion no es un error de la aplicacion: se puede navegar el mapa
        // sin haberse inscrito.
        return null;
      }
    },
    staleTime: 60_000,
    retry: false,
  });
}

export function usePuntos() {
  const idioma = useIdioma();
  return useQuery<PuntoPublico[]>({
    queryKey: ['puntos', idioma],
    queryFn: () => api.get<PuntoPublico[]>('/api/puntos'),
    staleTime: 5 * 60_000,
  });
}

export function useRecorrido(activo: boolean) {
  const idioma = useIdioma();
  return useQuery<EstadoRecorrido>({
    queryKey: ['recorrido', idioma],
    queryFn: () => api.get<EstadoRecorrido>('/api/mi-recorrido'),
    enabled: activo,
    staleTime: 15_000,
  });
}

/**
 * Datos publicos del evento, por su CODIGO y no por su identificador numerico:
 * es lo que va en la URL del cartel. El ranking sigue yendo por id porque lo
 * pide el propio cliente con el evento que ya conoce por su sesion, y esa
 * direccion no la teclea nadie.
 */
export function useEvento(codigo: string | null) {
  const idioma = useIdioma();
  return useQuery<EventoPublico>({
    queryKey: ['evento', codigo, idioma],
    queryFn: () => api.get<EventoPublico>(`/api/eventos/${codigo}`),
    enabled: codigo !== null,
  });
}

export function useRanking(eventoId: number | null, refrescoAutomatico: boolean) {
  const idioma = useIdioma();
  return useQuery<Ranking>({
    queryKey: ['ranking', eventoId, idioma],
    queryFn: () => api.get<Ranking>(`/api/eventos/${eventoId}/ranking`),
    enabled: eventoId !== null,
    // Durante el evento el ranking se mueve; fuera del evento no tiene sentido
    // consultar cada pocos segundos.
    refetchInterval: refrescoAutomatico ? 30_000 : false,
  });
}

export function usePregunta(puntoId: number | null, habilitada: boolean) {
  const idioma = useIdioma();
  return useQuery<PreguntaPublica>({
    queryKey: ['pregunta', puntoId, idioma],
    queryFn: () => api.get<PreguntaPublica>(`/api/puntos/${puntoId}/pregunta`),
    enabled: puntoId !== null && habilitada,
    retry: false,
  });
}

export function useRegistrarVisita() {
  const cliente = useQueryClient();
  return useMutation<RespuestaVisita, Error, DatosVisita>({
    mutationFn: (datos) => api.post<RespuestaVisita>('/api/visitas', datos),
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: ['recorrido'] });
    },
  });
}

export function useEnviarRespuesta() {
  const cliente = useQueryClient();
  return useMutation<ResultadoRespuesta, Error, DatosRespuesta>({
    mutationFn: (datos) => api.post<ResultadoRespuesta>('/api/respuestas', datos),
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: ['recorrido'] });
      void cliente.invalidateQueries({ queryKey: ['ranking'] });
      void cliente.invalidateQueries({ queryKey: ['pregunta'] });
    },
  });
}

export function useRegistrarse(codigo: string) {
  const cliente = useQueryClient();
  return useMutation<RespuestaRegistro, Error, DatosRegistro>({
    mutationFn: (datos) => api.post<RespuestaRegistro>(`/api/eventos/${codigo}/registro`, datos),
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: ['sesion'] });
    },
  });
}

export function useCerrarSesion() {
  const cliente = useQueryClient();
  return useMutation<void, Error, void>({
    mutationFn: () => api.borrar<void>('/api/sesion'),
    onSuccess: () => {
      void cliente.invalidateQueries();
    },
  });
}
